// src/components/cases/NewCaseModal.js
import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, SquarePen, Save } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import * as RadioGroup from '@radix-ui/react-radio-group';
import InputField from '../ui/InputField';
import IconButton from '../ui/IconButton';
import ExhibitTypeSwitch from '../ui/ExhibitTypeSwitch';
import StampPreview from '../stamp/StampPreview';
import EvidenceDataService from '../../services/EvidenceDataService';
import { ReactComponent as Briefcase } from '../../assets/icons/briefcase.svg';


/**
 * Two-page modal for creating a new case with stamp configuration
 * Page 1: Case name and number
 * Page 2: Stamp configuration (party type, colors, exhibit labels, transparency)
 */

const NewCaseModal = ({ 
  isOpen, 
  onClose, 
  onAddCase, 
  onUpdateCase,
  existingCase = null,
  mode = 'create' // 'create' or 'edit'
}) => {
  // Page state
  const isEditMode = mode === 'edit' && existingCase;
  const [currentPage, setCurrentPage] = useState(isEditMode ? 2 : 1);
  
  // Page 1 state
  const [caseName, setCaseName] = useState('');
  const [caseNumber, setCaseNumber] = useState('');
  const [errors, setErrors] = useState({});
  
  // Page 2 stamp configuration state
  const [partyType, setPartyType] = useState('plaintiff');
  const [customPartyType, setCustomPartyType] = useState('');
  const [exhibitType, setExhibitType] = useState('numbers');
  const [startingValue, setStartingValue] = useState('1');
  const [color, setColor] = useState('#E2DA74'); // Yellow default
  const [transparency, setTransparency] = useState('none');

  const [isOtherInputFocused, setIsOtherInputFocused] = useState(false);
const [isOtherInputHovered, setIsOtherInputHovered] = useState(false);
  
  // Focus state for helper text
  const [isStartingValueFocused, setIsStartingValueFocused] = useState(false);

  // Track if we've already initialized form for this modal opening
  const hasInitializedRef = useRef(false);
  const modalKeyRef = useRef(0);

  // Color options for stamp
  const colorOptions = [
    { value: '#FFFFFF', label: 'White', name: 'White' },
    { value: '#E2DA74', label: 'Yellow', name: 'Yellow' },
    { value: '#D56767', label: 'Red', name: 'Red' },
    { value: '#4A93CE', label: 'Blue', name: 'Blue' },
    { value: '#4BCE8C', label: 'Green', name: 'Green' }
  ];

  // Initialize or reset form when modal opens or case changes
  useEffect(() => {
    if (isOpen) {

      // Increment counter to force fresh component mount
      modalKeyRef.current += 1;

      // Only initialize if we haven't already for this opening
      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;

        if (isEditMode && existingCase) {
          // Set page 1 data
          setCaseName(existingCase.name || '');
          setCaseNumber(existingCase.caseNumber || '');

          // Set page 2 data from stampConfig
          const config = existingCase.stampConfig || {};
          setPartyType(config.partyType || 'plaintiff');
          setCustomPartyType(config.customPartyType || '');
          setExhibitType(config.exhibitType || 'numbers');
          setStartingValue(config.startingValue || (config.exhibitType === 'letters' ? 'A' : '1'));
          setColor(config.color || '#E2DA74');
          setTransparency(config.transparency || 'none');
          setCurrentPage(2);
        } else {
          // Reset to defaults for new case
          setCurrentPage(1);
          setCaseName('');
          setCaseNumber('');
          setErrors({});
          // Reset stamp config to defaults
          setPartyType('plaintiff');
          setCustomPartyType('');
          setExhibitType('numbers');
          setStartingValue('1');
          setColor('#E2DA74');
          setTransparency('none');
          setIsStartingValueFocused(false);
        }
      }
    } else {
      // Reset the initialization flag when modal closes
      hasInitializedRef.current = false;
    }
  }, [isOpen]);
  
  // Clear errors when fields are updated
  useEffect(() => {
    setErrors(prev => {
      const newErrors = { ...prev };
      let hasChanges = false;
      
      // Clear caseName error if field now has content
      if (prev.caseName && caseName.trim()) {
        delete newErrors.caseName;
        hasChanges = true;
      }
      
      // Clear caseNumber error if field now has content
      if (prev.caseNumber && caseNumber.trim()) {
        delete newErrors.caseNumber;
        hasChanges = true;
      }
      
      // Only update state if something changed to avoid unnecessary re-renders
      return hasChanges ? newErrors : prev;
    });
  }, [caseName, caseNumber]);
  
  // Handle moving to page 2 (Build Stamp button)
  const handleBuildStamp = (e) => {
    e.preventDefault();
    
    // Validate page 1
    const newErrors = {};
    if (!caseName.trim()) {
      newErrors.caseName = 'Please provide a case name.';
    }
    if (!caseNumber.trim()) {
      newErrors.caseNumber = 'Please provide a case number.';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Move to page 2
    setCurrentPage(2);
  };
  
  // Handle edit (back to page 1)
  const handleEdit = () => {
    setCurrentPage(1);
  };
  
  // Handle exhibit type toggle
  const handleExhibitTypeToggle = (checked) => {
    const newType = checked ? 'letters' : 'numbers';
    setExhibitType(newType);
    // Reset starting value to default for the new type
    setStartingValue(newType === 'letters' ? 'A' : '1');
  };
  
  // Handle final case creation or update
  const handleSubmit = () => {
    // Build stamp config
    const stampConfig = {
      partyType,
      customPartyType,
      exhibitType,
      startingValue,
      color,
      transparency
    };
    
    // Validate stamp config
    const validatedConfig = EvidenceDataService.validateStampConfig(stampConfig);
    
    if (isEditMode) {
      // Update existing case
      const updates = {
        name: caseName.trim(),
        caseNumber: caseNumber.trim(),
        stampConfig: validatedConfig,
        lastModified: new Date().toISOString()
      };
      
      console.log('Updating case with data:', updates);
      onUpdateCase(updates);
    } else {
      // Create new case
      const newCase = {
        name: caseName.trim(),
        caseNumber: caseNumber.trim(),
        status: 'active',
        createdAt: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        archived: false,
        documentCount: 0,
        stampConfig: validatedConfig
      };
      
      console.log('Creating new case with data:', newCase);
      onAddCase(newCase);
    }
  };
  
  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        {/* Backdrop/Overlay - pointer-events: none allows clicks to pass through to higher z-index elements */}
        <Dialog.Overlay 
          className="fixed inset-0 z-[200] bg-black/40"
          style={{
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)'
          }}
        />
        
        {/* Container for both modal content AND close button */}
        <div className="fixed left-1/2 top-1/2 z-[201] -translate-x-1/2 -translate-y-1/2">
          {/* Close button - OUTSIDE the Dialog.Content, with explicit pointer-events: auto */}
          <Dialog.Close asChild>
            <button
              className="group absolute w-comp-3xl h-comp-3xl rounded-full border border-app-gray-900 bg-app-white hover:bg-app-gray-300 transition-colors duration-app-normal flex items-center justify-center z-10"
              title="Close"
              style={{
                top: '-10px',
                right: '-10px',
                pointerEvents: 'auto' // Explicitly enable pointer events
              }}
            >
              {/* X shape */}
              <svg 
                width="12" 
                height="12" 
                viewBox="0 0 12 12" 
                className="text-app-gray-900"
              >
                <path 
                  d="M1 1L11 11M11 1L1 11" 
                  stroke="currentColor" 
                  strokeWidth="1" 
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </Dialog.Close>

          {/* Modal Content - also needs pointer-events: auto */}
          <Dialog.Content
            key={`modal-${modalKeyRef.current}`}
            className="relative bg-app-white rounded-app-lg border border-app-gray-900 px-app-4xl py-app-3xl focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
            style={{ 
              width: currentPage === 1 ? '320px' : '600px',
              height: '550px',
              maxHeight: '90vh',
              overflow: 'hidden',
              transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              pointerEvents: 'auto' // Explicitly enable pointer events
            }}
          >
            {/* Pages Container with sliding animation */}
            <div
              className="relative w-full h-full"
              style={{
                transform: currentPage === 1 ? 'translateX(0)' : 'translateX(-100%)',
                transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              {/* Page 1: Case Name and Number */}
              <div 
                className="absolute top-0 left-0 w-full h-full flex flex-col"
                style={{
                  transform: 'translateX(0)',
                  opacity: currentPage === 1 ? 1 : 0,
                  transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                <Dialog.Title className="text-app-2xl font-medium text-app-gray-900 mb-app-2xl">
                  {isEditMode ? 'Case Details' : 'New Case'}
                </Dialog.Title>
                
                <form onSubmit={handleBuildStamp} className="flex-1 flex flex-col">
                  <div className="space-y-app-xl">
                    <InputField
                      id="caseName"
                      label="Case Name"
                      value={caseName}
                      onChange={(e) => setCaseName(e.target.value)}
                      placeholder="Enter case name"
                      required
                      error={errors.caseName}
                      style={{ width: '192px' }}
                    />

                    <InputField
                      id="caseNumber"
                      label="Case Number"
                      value={caseNumber}
                      onChange={(e) => setCaseNumber(e.target.value)}
                      placeholder="Enter case number"
                      required
                      error={errors.caseNumber}
                      style={{ width: '192px' }}
                    />
                  </div>

                  <div className="flex justify-end mt-auto -mr-app-xl">
                    <button
                      type="submit"
                      className="btn-primary flex items-center gap-app-md"
                    >
                      {isEditMode ? 'Update Stamp' : 'Build Stamp'}
                      <ChevronRight className="w-comp-lg h-comp-lg" />
                    </button>
                  </div>
                </form>
              </div>

              {/* Page 2: Stamp Configuration */}
              <div 
                className="absolute top-0 left-full w-full h-full flex flex-col"
                style={{
                  opacity: currentPage === 2 ? 1 : 0,
                  transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                {/* Header with case info and edit button */}
                <div className="mb-app-2xl">
                  <div className="flex items-center">
                    <h2 className="text-app-2xl font-medium text-app-gray-900">
                      {caseName}
                    </h2>
                    <IconButton 
                      onClick={handleEdit}
                      title="Edit case info"
                      className="ml-app-lg mt-app-xs"
                      size="md"
                    >
                      <SquarePen strokeWidth={2} />
                    </IconButton>
                  </div>
                  <p className="text-app-sm text-app-gray-500">
                    {caseNumber}
                  </p>
                </div>

                {/* 2x2 Grid Layout */}
                <div className="space-y-0 mb-app-md flex-1 overflow-visible p-[2px] -m-[2px]">
                  {/* Top Row */}
                  <div className="grid grid-cols-2 gap-app-4xl">
                    {/* Top Left: Client Type */}
                    <div>
                      <label className="text-app-xs text-app-gray-600 font-medium mb-app-sm block">
                        Client Type
                      </label>
                      <RadioGroup.Root
                        value={partyType}
                        onValueChange={setPartyType}
                        className="space-y-0"
                      >
                        <div className="flex items-center h-[32px]">
                          <RadioGroup.Item
                            value="plaintiff"
                            id="plaintiff"
                            className="w-comp-xl h-comp-xl rounded-full border border-app-gray-400 hover:border-app-gray-900 data-[state=checked]:border-app-gray-900 transition-all duration-200 flex items-center justify-center relative flex-shrink-0"
                          >
                            <div 
                              className="w-comp-md h-comp-md rounded-full bg-app-gray-900 transition-transform duration-200 ease-out"
                              style={{
                                transform: partyType === 'plaintiff' ? 'scale(1)' : 'scale(0)',
                              }}
                            />
                          </RadioGroup.Item>
                          <label htmlFor="plaintiff" className="ml-app-xl text-app-md text-app-gray-800 cursor-pointer">
                            Plaintiff
                          </label>
                        </div>
                        
                        <div className="flex items-center h-[32px]">
                          <RadioGroup.Item
                            value="defendant"
                            id="defendant"
                            className="w-comp-xl h-comp-xl rounded-full border border-app-gray-400 hover:border-app-gray-900 data-[state=checked]:border-app-gray-900 transition-all duration-200 flex items-center justify-center relative flex-shrink-0"
                          >
                            <div 
                              className="w-comp-md h-comp-md rounded-full bg-app-gray-900 transition-transform duration-200 ease-out"
                              style={{
                                transform: partyType === 'defendant' ? 'scale(1)' : 'scale(0)',
                              }}
                            />
                          </RadioGroup.Item>
                          <label htmlFor="defendant" className="ml-app-xl text-app-md text-app-gray-800 cursor-pointer">
                            Defendant
                          </label>
                        </div>
                        
                        <div className="flex items-center h-[32px]">
                          <RadioGroup.Item
                            value="petitioner"
                            id="petitioner"
                            className="w-comp-xl h-comp-xl rounded-full border border-app-gray-400 hover:border-app-gray-900 data-[state=checked]:border-app-gray-900 transition-all duration-200 flex items-center justify-center relative flex-shrink-0"
                          >
                            <div 
                              className="w-comp-md h-comp-md rounded-full bg-app-gray-900 transition-transform duration-200 ease-out"
                              style={{
                                transform: partyType === 'petitioner' ? 'scale(1)' : 'scale(0)',
                              }}
                            />
                          </RadioGroup.Item>
                          <label htmlFor="petitioner" className="ml-app-xl text-app-md text-app-gray-800 cursor-pointer">
                            Petitioner
                          </label>
                        </div>
                        
                        <div className="flex items-center h-[32px]">
                          <RadioGroup.Item
                            value="respondent"
                            id="respondent"
                            className="w-comp-xl h-comp-xl rounded-full border border-app-gray-400 hover:border-app-gray-900 data-[state=checked]:border-app-gray-900 transition-all duration-200 flex items-center justify-center relative flex-shrink-0"
                          >
                            <div 
                              className="w-comp-md h-comp-md rounded-full bg-app-gray-900 transition-transform duration-200 ease-out"
                              style={{
                                transform: partyType === 'respondent' ? 'scale(1)' : 'scale(0)',
                              }}
                            />
                          </RadioGroup.Item>
                          <label htmlFor="respondent" className="ml-app-xl text-app-md text-app-gray-800 cursor-pointer">
                            Respondent
                          </label>
                        </div>
                        
                        <div className="flex items-center h-[32px]">
                          <RadioGroup.Item
                            value="other"
                            id="other"
                            className="w-comp-xl h-comp-xl rounded-full border border-app-gray-400 hover:border-app-gray-900 data-[state=checked]:border-app-gray-900 transition-all duration-200 flex items-center justify-center relative flex-shrink-0"
                          >
                            <div 
                              className="w-comp-md h-comp-md rounded-full bg-app-gray-900 transition-transform duration-200 ease-out"
                              style={{
                                transform: partyType === 'other' ? 'scale(1)' : 'scale(0)',
                              }}
                            />
                          </RadioGroup.Item>
                          <div className="ml-app-xl flex items-center">
                            {partyType === 'other' ? (
                              <div
                                className="relative rounded-full bg-app-white transition-all duration-app-normal"
                                style={{ 
                                  width: '160px',
                                  height: '32px',
                                  boxShadow: isOtherInputFocused 
                                    ? '0 0 0 2px #0C141D' 
                                    : '0 0 0 1px #0C141D',
                                }}
                                onMouseEnter={() => setIsOtherInputHovered(true)}
                                onMouseLeave={() => setIsOtherInputHovered(false)}
                              >
                                {/* Background pill layer */}
                                <div 
                                  className={`
                                    absolute rounded-full bg-app-gray-300
                                    ${isOtherInputFocused ? 'transition-all duration-app-normal' : ''}
                                    ${isOtherInputFocused 
                                      ? 'inset-0' 
                                      : isOtherInputHovered
                                        ? 'inset-[3px]' 
                                        : 'inset-0 opacity-0'
                                    }
                                  `}
                                />
                                
                                {/* Input element */}
                                <input
                                  type="text"
                                  value={customPartyType}
                                  onChange={(e) => setCustomPartyType(e.target.value)}
                                  onFocus={() => setIsOtherInputFocused(true)}
                                  onBlur={() => setIsOtherInputFocused(false)}
                                  placeholder="Enter type"
                                  maxLength={20}
                                  autoFocus
                                  className={`
                                    relative z-10 w-full h-full rounded-full px-app-lg 
                                    bg-transparent text-app-sm text-app-gray-900 font-normal
                                    placeholder:font-normal outline-none border-none
                                    transition-colors duration-app-normal
                                    ${isOtherInputHovered || isOtherInputFocused 
                                      ? 'placeholder:text-app-gray-500' 
                                      : 'placeholder:text-app-gray-400'
                                    }
                                  `}
                                />
                              </div>
                            ) : (
                              <label htmlFor="other" className="text-app-md text-app-gray-800 cursor-pointer">
                                Other
                              </label>
                            )}
                          </div>
                        </div>
                      </RadioGroup.Root>
                    </div>

                    {/* Top Right: Stamp Preview + Color Picker */}
                    <div>
                      {/* Stamp preview in graph paper container - centered */}
                      <div className="bg-graph-paper p-app-lg rounded-app-md mb-app-2xl flex items-center justify-center min-h-[120px]">
                        <div className="transition-all duration-300 ease-out">
                          <StampPreview
                            config={{
                              partyType,
                              customPartyType,
                              exhibitType,
                              startingValue,
                              color,
                              transparency
                            }}
                            caseData={{
                              name: caseName,
                              caseNumber: caseNumber
                            }}
                            documentIndex={0}
                            scale={1.5}
                          />
                        </div>
                      </div>
                      
                      {/* Color picker with ring effects */}
                      <div className="flex gap-app-sm justify-center">
                        {colorOptions.map((colorOption) => (
                          <button
                            key={colorOption.value}
                            onClick={() => setColor(colorOption.value)}
                            className={`flex-shrink-0 rounded-md transition-all duration-app-normal shadow-app-inner-xs ${
                              color === colorOption.value 
                                ? 'ring-1 ring-app-gray-900 ring-offset-2' 
                                : 'ring-1 ring-transparent hover:ring-app-gray-900'
                            }`}
                            style={{ 
                              backgroundColor: colorOption.value,
                              width: '2rem',
                              height: '2rem'
                            }}
                            title={colorOption.name}
                            aria-label={`Select ${colorOption.name} color`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Bottom Row */}
                  <div className="grid grid-cols-2 gap-app-4xl">
                    {/* Bottom Left: Exhibit Labels */}
                    <div>
                      <label className="text-app-xs text-app-gray-600 font-medium mb-app-md block mt-app-xl">
                        Exhibit Labels
                      </label>
                      <div className="flex items-center gap-app-lg">
                        {/* Starting value input with helper text on focus */}
                        <div className="relative">
                          <InputField
                            value={startingValue}
                            onChange={(e) => setStartingValue(e.target.value)}
                            onFocus={() => setIsStartingValueFocused(true)}
                            onBlur={() => setIsStartingValueFocused(false)}
                            placeholder={exhibitType === 'letters' ? 'A' : '1'}
                            maxWidth="60px"
                            height="29px"

                          />
                          {/* Helper text shown on focus */}
                          {isStartingValueFocused && (
                            <p className="text-app-gray-400 text-app-2xs mt-app-xs absolute left-0 top-full whitespace-nowrap">
                              Starting value
                            </p>
                          )}
                        </div>
                        
                        {/* Custom 3D switch */}
                        <ExhibitTypeSwitch
                          checked={exhibitType === 'letters'}
                          onCheckedChange={handleExhibitTypeToggle}
                        />
                      </div>
                    </div>

                    {/* Bottom Right: Transparency */}
                    <div>
                      <label className="text-app-xs text-app-gray-600 font-medium mb-app-xs block mt-app-xl">
                        Transparency
                      </label>
                      <div className="relative pt-2 pb-6">
                        {/* Line behind circles - 1px app-gray-400 with 8px overhang on each side */}
                        <div 
                          className="absolute top-4 bg-app-gray-400 h-px"
                          style={{ 
                            left: '-8px',
                            right: '-8px'
                          }}
                        />
                        
                        {/* Radio group with circles and labels */}
                        <RadioGroup.Root
                          value={transparency}
                          onValueChange={setTransparency}
                          className="flex justify-between relative z-10"
                        >
                          <div className="flex flex-col items-center">
                            <RadioGroup.Item
                              value="none"
                              id="trans-none"
                              className="w-comp-xl h-comp-xl rounded-full border border-app-gray-400 bg-white hover:border-app-gray-900 data-[state=checked]:border-app-gray-900 transition-all duration-200 flex items-center justify-center"
                            >
                              <div 
                                className="w-comp-md h-comp-md rounded-full bg-app-gray-900 transition-transform duration-200 ease-out"
                                style={{
                                  transform: transparency === 'none' ? 'scale(1)' : 'scale(0)',
                                }}
                              />
                            </RadioGroup.Item>
                            <label htmlFor="trans-none" className="mt-2 text-app-xs text-app-gray-600 cursor-pointer">
                              None
                            </label>
                          </div>
                          
                          <div className="flex flex-col items-center">
                            <RadioGroup.Item
                              value="low"
                              id="trans-low"
                              className="w-comp-xl h-comp-xl rounded-full border border-app-gray-400 bg-white hover:border-app-gray-900 data-[state=checked]:border-app-gray-900 transition-all duration-200 flex items-center justify-center"
                            >
                              <div 
                                className="w-comp-md h-comp-md rounded-full bg-app-gray-900 transition-transform duration-200 ease-out"
                                style={{
                                  transform: transparency === 'low' ? 'scale(1)' : 'scale(0)',
                                }}
                              />
                            </RadioGroup.Item>
                            <label htmlFor="trans-low" className="mt-2 text-app-xs text-app-gray-600 cursor-pointer">
                              Low
                            </label>
                          </div>
                          
                          <div className="flex flex-col items-center">
                            <RadioGroup.Item
                              value="high"
                              id="trans-high"
                              className="w-comp-xl h-comp-xl rounded-full border border-app-gray-400 bg-white hover:border-app-gray-900 data-[state=checked]:border-app-gray-900 transition-all duration-200 flex items-center justify-center"
                            >
                              <div 
                                className="w-comp-md h-comp-md rounded-full bg-app-gray-900 transition-transform duration-200 ease-out"
                                style={{
                                  transform: transparency === 'high' ? 'scale(1)' : 'scale(0)',
                                }}
                              />
                            </RadioGroup.Item>
                            <label htmlFor="trans-high" className="mt-2 text-app-xs text-app-gray-600 cursor-pointer">
                              High
                            </label>
                          </div>
                          
                          <div className="flex flex-col items-center">
                            <RadioGroup.Item
                              value="full"
                              id="trans-full"
                              className="w-comp-xl h-comp-xl rounded-full border border-app-gray-400 bg-white hover:border-app-gray-900 data-[state=checked]:border-app-gray-900 transition-all duration-200 flex items-center justify-center"
                            >
                              <div 
                                className="w-comp-md h-comp-md rounded-full bg-app-gray-900 transition-transform duration-200 ease-out"
                                style={{
                                  transform: transparency === 'full' ? 'scale(1)' : 'scale(0)',
                                }}
                              />
                            </RadioGroup.Item>
                            <label htmlFor="trans-full" className="mt-2 text-app-xs text-app-gray-600 cursor-pointer">
                              Full
                            </label>
                          </div>
                        </RadioGroup.Root>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Create Case button */}
                <div className="flex justify-end -mr-app-xl">
                  <button
                    onClick={handleSubmit}
                    className="btn-primary flex items-center gap-app-md"
                  >
                    {isEditMode ? <Save className="w-comp-lg h-comp-lg text-app-white"/> : <Briefcase className="w-comp-lg h-comp-lg text-app-white"/>}
                    {isEditMode ? 'Save Changes' : 'Create Case'}
                  </button>
                </div>
              </div>
            </div>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default NewCaseModal;